import { SlideStatement } from "../../../SlideStatement";
import { SupLink } from "../../../SlideContext";

// Use of Funds (split from the old slide 19, TPF 2026-09-04). The category
// split is of the full 24-month spend, not of the raise: revenue covers ~105%,
// the €1.25M covers the cumulative deficit and the downside, with ~€580K to spare. Numbers are the
// Pre-Seed Use of Funds Model sheet, base scenario, snapshot 2026-09-04.
interface Line {
  label: string;
  amount: string;
}
interface Category {
  pct: string;
  label: string;
  amount: string;
  sub?: string;
  lines?: Line[];
}

// Five tiles, of the €3.03M 24-month spend (PM/QE/DevOps cut; ad ramp,
// brand programs, SOC 2 and hardware added 2026-09-05). Infra/COGS is its own tile
// (TPF, 2026-09-05) so Product & eng reads as people only. Sub-lines are the
// sheet's own rows regrouped: GTM = programs + PMM/MOps (brand), ads +
// affiliate share (performance), BD base + commission; Infra fixed = AI
// tooling + platform + security review, variable = audit COGS + payment
// processing; G&A = desk tech + travel vs legal/accounting + raise counsel.
const CATEGORIES: Category[] = [
  {
    pct: "52%",
    label: "Go-to-market",
    amount: "€1.57M",
    lines: [
      { label: "Brand — PMM · MOps · programs", amount: "€628K" },
      { label: "Performance — ads · affiliates", amount: "€702K" },
      { label: "BD — base · commission", amount: "€244K" },
    ],
  },
  {
    pct: "5%",
    label: "Product & eng",
    amount: "€150K",
    sub: "Thomas + AI agents build it; two part-time experts",
    lines: [
      { label: "Design (PT)", amount: "€75K" },
      { label: "ASO experts (PT)", amount: "€75K" },
    ],
  },
  {
    pct: "7%",
    label: "Infra / COGS",
    amount: "€201K",
    lines: [
      { label: "Fixed — tooling · platform · security", amount: "€54K" },
      { label: "Variable — audit COGS · payments", amount: "€148K" },
    ],
  },
  {
    pct: "32%",
    label: "Founders",
    amount: "€977K",
    sub: "€180K/yr gross each",
    lines: [
      { label: "Thomas, CEO", amount: "€489K" },
      { label: "Yevhen, CMO", amount: "€489K" },
    ],
  },
  {
    pct: "4%",
    label: "G&A",
    amount: "€127.5K",
    lines: [
      { label: "HW · office · admin", amount: "€50K" },
      { label: "Prof. services — legal · tax · SOC 2", amount: "€77.5K" },
    ],
  },
];

export default function UseOfFunds() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="Use of Funds">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            A go-to-market round.
          </h1>
          {/* Restates what the headline means by "go-to-market" — above the
              rule with it. */}
          <p className="mt-4 text-xl font-medium tracking-tight text-slate-600 md:mt-5 md:text-2xl">
            Post-AI, seed capital buys cross-functional founders and
            go-to-market — 84% of the €3.0M plan. Revenue covers all of it; the
            €1.25M<SupLink to="the-raise">18</SupLink> funds year one.
          </p>
        </div>
      </SlideStatement>

      {/* Below the rule — where the money goes, why engineering is cheap, and
          what month 24 looks like. */}
      <div className="flex flex-col gap-5 md:min-h-0 md:flex-1 md:justify-between md:gap-2.5 md:pt-6">

      {/* Month 24 — the target. First under the rule (TPF, 2026-09-09): the
          destination frames the spend, so the reader meets it before the
          allocation. Plain panel, not inverted — an inverted bar reads as a
          closing verdict, and this opens. ARR and growth are the sheet's base
          case; the valuation is an explicit multiple on ARR so the arithmetic
          is on the slide, not implied. Entry post-money €8.3–10.4M at 12–15%. */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
          Month 24 — the target
        </p>
        <div className="mt-3 grid grid-cols-1 gap-4 rounded-xl bg-slate-50 px-6 py-2.5 ring-1 ring-slate-200 md:grid-cols-3 md:gap-6 md:px-8">
          <Swing value="€3.7M" label="ARR" sub="base plan, cash-flow positive" />
          <Swing value="2.8×" label="year-over-year growth" sub="at that point, on revenue" />
          <Swing
            value="€30–45M"
            label="implied valuation"
            sub="at 8–12× ARR — 3–5× today's post-money"
            accent
          />
        </div>
      </div>

        {/* The split — of total spend, founder pay in the open */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
          Where the 24-month spend goes
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5 md:gap-3">
          {CATEGORIES.map((c) => (
            <div
              key={c.label}
              className="flex flex-col rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200 md:p-3"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-900 md:text-[13px]">
                {c.label}
              </p>
              <p className="mt-1 flex items-baseline gap-x-2">
                <span className="text-3xl font-bold tracking-tight text-slate-900 md:text-3xl">
                  {c.pct}
                </span>
                <span className="text-sm font-normal text-slate-500 md:text-base">
                  {c.amount}
                </span>
              </p>
              {c.sub ? (
                <p className="mt-1.5 text-xs leading-snug text-slate-500 md:text-sm">
                  {c.sub}
                </p>
              ) : null}
              {c.lines ? (
                <ul className="mt-2 divide-y divide-slate-200 border-t border-slate-200 text-xs text-slate-600 md:text-[13px]">
                  {c.lines.map((l) => (
                    <li key={l.label} className="flex items-baseline justify-between gap-2 py-[3px]">
                      <span className="leading-snug">{l.label}</span>
                      <span className="shrink-0 tabular-nums font-medium text-slate-900">
                        {l.amount}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      </div>

        {/* Founder leverage — the engineering org design behind the numbers */}
        <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
          Founder × AI leverage — engineering is one founder plus €54K of tooling; product & eng is 5% of spend, not 40%+
        </p>
        <div className="mt-3 rounded-2xl bg-blue-50 px-6 py-2 ring-1 ring-blue-200 md:px-8">
          <p className="text-sm leading-snug text-slate-700 md:text-base">
            Thomas (CEO) is also the CTO and the entire engineering, QA and
            ops team. AI agents author 100%
            of the platform's code and ship it to production nightly through
            the agentic software factory Thomas engineered and oversees.{" "}
            <span className="font-semibold text-slate-900">
              The factory is a built asset, not a cost line.
            </span>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

function Swing({
  value,
  label,
  sub,
  accent,
}: {
  value: string;
  label: string;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div>
      <p
        className={`text-4xl font-bold leading-none tracking-tight md:text-4xl ${
          accent ? "text-blue-600" : "text-slate-900"
        }`}
      >
        {value}
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-900 md:text-base">{label}</p>
      <p className="text-xs text-slate-500 md:text-sm">{sub}</p>
    </div>
  );
}
